import { useState, useEffect } from "react";
import fetchWithCredentials, { FetchWithCredentialsError } from "apiClient";
import Message from "types/Message";
import { ensureMessageListKey } from "utils/message";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export function useTaskChat(
  taskId: number | null,
  page: number
): {
  messages: Message[];
  loading: boolean;
  error: string | null;
  next: string | null;
  sendMessage: (content: string, fileUrl?: string | null) => Promise<boolean>;
} {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [next, setNext] = useState<string | null>(null);

  useEffect(() => {
    if (!taskId) return;

    const fetchMessages = async () => {
      setLoading(true);
      setError(null);
      try {
        const data: PaginatedResponse<Message> = await fetchWithCredentials(
          `/manager-api/tasks/${taskId}/chat/?page=${page}`
        );
        setMessages(data.results.map(ensureMessageListKey));
        setNext(data.next);
      } catch (err: any) {
        console.error(err);
        setError(err.message || "Error fetching task messages");
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [taskId, page]);

  const sendMessage = async (
    content: string,
    fileUrl?: string | null
  ): Promise<boolean> => {
    const body: any = { content };
    if (fileUrl) body.fileUrl = fileUrl;
    setError(null);
    try {
      await fetchWithCredentials(`/manager-api/tasks/${taskId}/chat/`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      return true;
    } catch (err: any) {
      const errorMessage = err.message || "Error sending task message";
      const statusCode =
        err instanceof FetchWithCredentialsError ? err.status : null;
      if (statusCode === null || statusCode < 500) {
        setError(errorMessage);
      }
      return false;
    }
  };

  return { messages, loading, error, next, sendMessage };
}
